import { type FC, type Ref, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import { Tooltip } from '../../tooltip/Tooltip';
import { formatDate, formatNumber } from '../helpers/formatters';
import type { DateFormat, TextProps, TextPropsWithLink } from '../helpers/types';
import { variantElementMap } from '../helpers/types';
import { getDisplayValue, getTruncateClasses, resolveColor } from '../helpers/utils';
import { textVariants } from '../helpers/variants';
import { ExternalLink } from './ExternalLink';
import { RouterLink } from './RouterLink';

/**
 * Figma の Text Style に基づいた統一されたテキストコンポーネント
 *
 * global.css で定義された Text Style（h1-h4、body、label、subhead、link、description）を提供し、
 * 数値フォーマット、内部リンク（TanStack Router）、外部リンクをサポートする。
 *
 * @example
 * // 基本的な使い方
 * <Text variant="h1">見出し</Text>
 * <Text variant="body">本文テキスト</Text>
 *
 * @example
 * // 数値フォーマット（千円単位）
 * <Text viewMode="number" unit="thousand">{1234567}</Text>
 * // 結果: "1,234千円"
 *
 * @example
 * // 日付フォーマット
 * <Text viewMode="date">{dateStr}</Text>
 * // 結果: "2024/01/15 10:30"
 *
 * @example
 * // 日付フォーマット（カスタムフォーマット）
 * <Text viewMode="date" format="YYYY年MM月DD日">{dateStr}</Text>
 * // 結果: "2024年01月15日"
 *
 * @example
 * // 内部リンク（TanStack Router）
 * <Text variant="link" to="/projects">プロジェクト一覧</Text>
 *
 * @example
 * // 外部リンク
 * <Text variant="link" href="https://example.com">外部サイト</Text>
 *
 * @example
 * // 省略表示（1行）
 * <Text truncate>長いテキストは省略されます...</Text>
 *
 * @example
 * // 省略表示（複数行）
 * <Text truncate={3}>3行を超えるテキストは省略されます...</Text>
 */
export const TextBase: FC<TextProps> = ({
  htmlFor,
  variant = 'body',
  color,
  as,
  children,
  className,
  onClick,
  id,
  truncate,
  enabledTooltip = true,
  ...props
}) => {
  const { t } = useTranslation();

  // フォーマット処理
  const formattedChildren = (() => {
    // 数値フォーマット
    if (props.viewMode === 'number' && typeof children === 'number') {
      return formatNumber(
        children,
        props.unit,
        t as (key: string, options?: Record<string, unknown>) => string,
      );
    }
    // 日付フォーマット
    if (props.viewMode === 'date' && typeof children === 'string') {
      const defaultFormat: DateFormat = 'YYYY/MM/DD HH:mm:ss';
      return formatDate(children, props.format ?? defaultFormat);
    }
    return children;
  })();

  // スタイル解決
  const resolvedColor = resolveColor(variant, color);
  const truncateClasses = getTruncateClasses(truncate);
  const baseClassName = cn(
    textVariants({ variant, color: resolvedColor }),
    truncateClasses,
    className,
  );

  // オーバーフロー検出（ホバー時に判定し、見切れている場合のみ Tooltip を表示する）
  const textRef = useRef<HTMLElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  // TanStack Router Link
  if ('to' in props && props.to) {
    return (
      <RouterLink props={props as TextPropsWithLink} className={baseClassName} onClick={onClick}>
        {formattedChildren}
      </RouterLink>
    );
  }

  //　フルページリロードが発生する。外部リンクなどの場合のみ使用する。
  if ('href' in props && props.href) {
    return (
      <ExternalLink
        href={props.href}
        openInNewTab={props.openInNewTab}
        className={baseClassName}
        onClick={onClick}
      >
        {formattedChildren}
      </ExternalLink>
    );
  }

  // Regular element
  const resolvedVariant = variant ?? 'body';
  const Element = as || variantElementMap[resolvedVariant];

  return (
    <Tooltip
      enabled={!!truncate && enabledTooltip && isOverflowing}
      tooltipContent={formattedChildren}
    >
      <Element
        ref={textRef as Ref<never>}
        className={cn(
          textVariants({ variant: resolvedVariant, color: resolvedColor }),
          // line-clamp は break が無いと長い連続文字列で折り返されず横にはみ出すため break-words を併用
          truncate ? cn(truncateClasses, 'break-words') : 'whitespace-pre-wrap break-words',
          'min-w-0',
          className,
        )}
        onClick={onClick}
        onMouseEnter={() => {
          const el = textRef.current;
          if (el) {
            setIsOverflowing(el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight);
          }
        }}
        id={id}
        htmlFor={htmlFor}
      >
        {getDisplayValue(formattedChildren)}
      </Element>
    </Tooltip>
  );
};
